import type { V1DirectoryListChildrenData } from '@repo/sdk/gen'
import type {
	SearchMiddleware,
	SearchSchemaInput
} from '@tanstack/react-router'
import { stripSearchParams } from '@tanstack/react-router'
import { z } from 'zod'
import type { TKey } from '@/lib/i18n'

/**
 * The categories in the order the panel offers them, which is the order the
 * server declares and sorts by — never the order their translated labels
 * happen to fall in.
 *
 * Above the schema and not with the other constants below it: the schema reads
 * it while this module is evaluated, and a `const` declared further down is not
 * there yet when it does.
 *
 * Written out rather than derived, because the schema needs the values at run
 * time and the contract only hands a type over. Nothing here asserts they are
 * the contract's, and nothing needs to: the schema built from them types
 * `FolderSearch['categories']`, which four call sites hand to the listing
 * factories, whose parameters read the accepted categories off the generated
 * request — so a category invented here fails to compile at every one of them.
 * The direction that has no such guard is the opposite one, a category the
 * server gains, and `CATEGORY_LABELS` below is where that is caught. It caught
 * this one: OPSSTD-8748 is QA reporting that the panel offered six kinds where
 * the product owns nine.
 *
 * **The last three are appended and not slotted in.** `presentation` reads as
 * if it belonged next to `document` and is at the end anyway, because this list
 * is the server's declared order and the server appends — see `NodeCategory` in
 * `backend/src/modules/directory/constants.ts` for why. Sorting this list to
 * taste would put the panel and the listing in two different orders.
 */
export const NODE_CATEGORIES = [
	'folder',
	'document',
	'image',
	'spreadsheet',
	'archive',
	'video',
	'audio',
	'presentation',
	'other'
] as const

/**
 * The columns a listing can be ordered by, and the two senses of an order.
 *
 * Named rather than written inline in the schema for `NODE_CATEGORIES`'s
 * reason with one addition: `readListingSearch` below has to decide whether a
 * value read out of an address is one of these, and a schema literal is not
 * something a function can ask. One list, read by the schema and by the
 * reader, is what keeps the two from drifting apart by an edit to one of them.
 */
export const SORT_KEYS = ['name', 'updatedAt', 'size', 'lastViewed'] as const

/**
 * The subset each kind of page accepts. `lastViewed` is not offered on the
 * favorites listing, so an address carrying it there is unreadable, not a
 * request the server would refuse on the second page.
 */
export const LISTING_SORT_KEYS = [
	'name',
	'updatedAt',
	'size',
	'lastViewed'
] as const satisfies readonly (typeof SORT_KEYS)[number][]

export const FAVORITES_SORT_KEYS = [
	'name',
	'updatedAt',
	'size'
] as const satisfies readonly (typeof SORT_KEYS)[number][]

export type ListingSortKey = (typeof LISTING_SORT_KEYS)[number]
export type FavoritesSortKey = (typeof FAVORITES_SORT_KEYS)[number]

export const SORT_DIRECTIONS = ['asc', 'desc'] as const

/**
 * How many people one listing may be narrowed to.
 *
 * **The contract's ceiling, not a taste of this screen's.** `list-children`
 * takes at most fifty `people` and `search-users` at most fifty `ids`, so the
 * fifty-first is not a filter drawn badly: it is a request the server refuses,
 * and the refusal takes the listing down with it rather than a name off a
 * chip. Written out because the generated request carries the bound as
 * documentation and not as a type — nothing here can read it.
 *
 * Enforced once, in `readPeople` below, which is the one gate both ways in:
 * the middlewares run it when the app builds an address, so a panel where
 * somebody ticked fifty-one names is cut to the same fifty as an address typed
 * by hand.
 */
const MAX_PEOPLE = 50

/**
 * How a listing is drawn — the order it is in and the filters narrowing it — as
 * the URL carries it.
 *
 * In the URL because it is shareable and must survive a reload, and shared by
 * every route that draws one — your own tree and one somebody shared with you
 * are the same screen at two paths, and «Condivisi con me» takes the same
 * four columns because `list-shared-with-me` accepts the same `sort` that
 * `list-children` does. A copy of this schema in each would be three contracts
 * that agree until one is edited.
 *
 * Every field defaults, which is what lets a link name one of those routes
 * without carrying a search object: the validator's input has no required key,
 * so `to='/shared'` in the navigation still type-checks.
 *
 * The literals are the client's half of a contract the SDK owns: they are
 * handed to `folderChildrenQueryOptions` and `sharedWithMeQueryOptions`, whose
 * parameters are typed from the generated requests, so a column the server
 * stops offering fails to compile rather than failing at runtime on the second
 * page.
 *
 * **`foldersFirst` is deliberately not here.** It still goes to the server and
 * still belongs to a cursor's identity — the server pins folders above the
 * files while it is on, so it changes the sequence a cursor indexes into
 * exactly as `by` and `direction` do — but it is a habit about how somebody
 * likes to look at their own files rather than a statement about the listing
 * being linked to, and a link carrying it would impose the sender's habit on
 * the reader. `use-folders-first.ts` holds it beside the grid-or-list
 * preference, `use-listing-sort.ts` reads it back into the sort, and it stays
 * in the query key — which is what keeps the cursors correct.
 *
 * An address that still carries `?foldersFirst=` from before loses it in
 * silence: the schema strips what it does not declare.
 *
 * **The filters live here with the order for the same reason, and they are the
 * one thing that is written whole rather than merged.** `categories` and
 * `people` are what «Applica» commits: until it is pressed the panel holds a
 * draft of its own and this says what the listing is actually showing.
 *
 * `people` carries the ids the wire carries and nothing else. It used to carry
 * `<subjectId>:<mode>` tokens, because each person was filtered in a sense of
 * their own and a URL that nests objects is a URL nobody can read or hand-fix;
 * OPSSTD-7193 took the sense out, so the two filters are now the same shape and
 * neither needs parsing on the way in.
 *
 * **The schema states the shape; it is not what reads an address.** Routes
 * validate with `validateListingSearch` and build with
 * `listingSearchMiddlewares`, both below, because an address is typed by
 * whoever wrote it and a schema that throws on a value it cannot read turns a
 * mistyped link into an error screen. This stays strict so that the type it
 * produces is exact, and it is the reader that is forgiving.
 */
export const folderSearchSchema = z.object({
	by: z.enum(SORT_KEYS).default('name'),
	categories: z.array(z.enum(NODE_CATEGORIES)).default([]),
	direction: z.enum(SORT_DIRECTIONS).default('asc'),
	people: z.array(z.string()).default([])
})

export type FolderSearch = z.infer<typeof folderSearchSchema>

/**
 * The listing as it is drawn when nobody has asked for anything: parsed out of
 * the schema rather than retyped beside it, so the defaults have one home and
 * a change to them cannot leave a second copy behind.
 *
 * Read by the order control, which tints itself only while the order is *not*
 * this one. That is the whole reason it is named: «an order is in force» is
 * meaningless on its own, because a listing always has one — what a person can
 * see at a glance is whether it is still the order they would have got without
 * touching anything.
 *
 * It answers for the address and for nothing else, which is the whole
 * comparison: `foldersFirst` left the address, so it left the tint with it. A
 * preference that is true of every listing somebody opens would keep the button
 * coloured for good, saying «something is different here» about the one thing
 * that never is.
 */
export const DEFAULT_LISTING_SEARCH = folderSearchSchema.parse({})

/**
 * Each category in the person's own words, as a filter names them: plural,
 * because ticking one narrows a listing to every node of that kind.
 *
 * **It is not the word for one node.** `TYPE_LABEL_KEYS` in `type-label.ts` is
 * that one, and the two are not interchangeable: a row or a panel describing a
 * single file wants «Presentazione», and this map says «Presentazioni».
 *
 * Here and not in the panel that draws them, because this module owns the
 * union they name: a second map somewhere else would be a second vocabulary,
 * agreeing with this one until somebody edits one of the two.
 *
 * An exhaustive map rather than a branch, so a category the contract adds is a
 * compile error here instead of a row wearing the previous one's name. Keyed
 * off the request's own union and not off `NODE_CATEGORIES`, which is what
 * makes that sentence true: keyed off the array, the map would only ever
 * restate a list this file wrote itself, and a category added on the server
 * would reach neither.
 *
 * There is no companion map for the people filter any more. It had one while
 * each person carried a sense of their own — «Tutto», «Proprietario»,
 * «Condiviso con» — and OPSSTD-7193 took the sense out: a person is now named
 * by their own name and nothing else needs translating.
 */
export const CATEGORY_LABELS = {
	archive: 'client:files.filters.category.archive',
	audio: 'client:files.filters.category.audio',
	document: 'client:files.filters.category.document',
	folder: 'client:files.filters.category.folder',
	image: 'client:files.filters.category.image',
	other: 'client:files.filters.category.other',
	presentation: 'client:files.filters.category.presentation',
	spreadsheet: 'client:files.filters.category.spreadsheet',
	video: 'client:files.filters.category.video'
} as const satisfies Record<
	NonNullable<
		NonNullable<V1DirectoryListChildrenData['query']>['categories']
	>[number],
	TKey
>

/**
 * A value an address asked for that the listing could not read, named by the
 * parameter it was written under.
 *
 * The raw value is kept although nothing on screen shows it: what is drawn is
 * the parameter's own name, because a value that could not be read is by
 * definition not a word the person recognises, and quoting it back would put
 * whatever was in the address on the screen.
 */
export interface UnreadableSearchValue {
	param: keyof FolderSearch
	value: unknown
}

/** A listing read out of an address, and what the address got wrong. */
export interface ListingSearchReading {
	search: FolderSearch
	unreadable: UnreadableSearchValue[]
}

/**
 * Reads an address into a listing: keeps every value it recognises, drops the
 * ones it does not, and hands back what it dropped so somebody can be told.
 *
 * **Forgiving on purpose, and this is the whole of OPSSTD-6606's fifth
 * point.** An address is the one part of this screen a person can type, and
 * the ones that arrive mistyped are the ones that were shared: a link pasted
 * into a chat and broken by the line wrap, a category renamed since the link
 * was written, a filter edited by hand. A schema that throws turns every one
 * of those into an error page over a folder that is perfectly readable. So the
 * unreadable half is discarded and the listing opens on the readable half.
 *
 * **It is also what makes two equal listings one address.** Both multi-value
 * filters come back in an order of their own and without repeats, so a set
 * ticked in one order and the same set ticked in another are the same string —
 * which is what lets `stripSearchParams` below recognise a default, and what
 * keeps the history from filling with entries that differ by nothing.
 *
 * Reading is separate from the schema above rather than folded into it with
 * `.catch()` per field: a caught field reports nothing, and «the listing
 * silently ignored what you asked for» is the failure this is here to prevent.
 */
export function readListingSearch(
	input: unknown,
	sortKeys: readonly FolderSearch['by'][] = LISTING_SORT_KEYS
): ListingSearchReading {
	const raw =
		typeof input === 'object' && input !== null
			? (input as Record<string, unknown>)
			: {}
	const unreadable: UnreadableSearchValue[] = []

	return {
		search: {
			by: readChoice('by', raw.by, sortKeys, unreadable),
			categories: readCategories(raw.categories, unreadable),
			direction: readChoice(
				'direction',
				raw.direction,
				SORT_DIRECTIONS,
				unreadable
			),
			people: readPeople(raw.people, unreadable)
		},
		unreadable
	}
}

/**
 * What the three listing routes validate their address with.
 *
 * The parameter is typed as the search a *link* may carry, not as a promise
 * about what arrives: the router hands this whatever was in the URL bar, which
 * is why the body treats it as unknown. Declaring it this way is what keeps
 * `<Link search={…}>` checked against the four parameters — the same claim a
 * Zod validator's input type makes, written out instead of inferred.
 *
 * `SearchSchemaInput` is how that is said. Without it the router reads a
 * function validator's *output* as the type a link must satisfy, and every
 * `navigate({ to: '/files/$nodeId' })` in the app would have to spell out all
 * four parameters to compile — which is the opposite of what defaults are for.
 * The marker is a type and nothing else: it is not in the value that arrives.
 */
export function validateListingSearch(
	search: Partial<FolderSearch> & SearchSchemaInput
): FolderListingSearch {
	// `by` is one of `LISTING_SORT_KEYS` by construction: `readChoice` drops
	// anything else.
	return readListingSearch(search).search as FolderListingSearch
}

/** The folder and shared listings' address. */
export type FolderListingSearch = Omit<FolderSearch, 'by'> & {
	by: ListingSortKey
}

/**
 * Whether an address is the favorites page's. For the one reader that has no
 * router to ask (the navigation entry, read outside React); everything inside
 * the router matches the route instead.
 */
export function isFavoritesPath(pathname: string) {
	return pathname.startsWith('/favorites')
}

/** The favorites page's address: no `lastViewed`. */
export type FavoritesSearch = Omit<FolderSearch, 'by'> & {
	by: FavoritesSortKey
}

/** `validateListingSearch`'s twin for the favorites page. */
export function validateFavoritesSearch(
	search: Partial<FolderSearch> & SearchSchemaInput
): FavoritesSearch {
	return readListingSearch(search, FAVORITES_SORT_KEYS)
		.search as FavoritesSearch
}

/**
 * What the three listing routes build their addresses with.
 *
 * Both are needed and neither does the other's job. The first rewrites the
 * search into the one form a given listing has — filters in a stable order,
 * no repeats — and the second then deletes what equals the default, which it
 * can only recognise once the first has run. Applied in this order by the
 * router, which chains them through `next`.
 *
 * This runs when the router *builds* an address, so it shapes what the app
 * produces and not what somebody typed. A hand-written address stays as it was
 * written until the next navigation, and is read by `validateListingSearch`
 * meanwhile — which is the right way round: rewriting the URL under somebody
 * on arrival would lose the address they still have in their clipboard.
 */
export const listingSearchMiddlewares: SearchMiddleware<FolderListingSearch>[] =
	[
		({ next, search }) =>
			next(readListingSearch(search).search as FolderListingSearch),
		stripSearchParams<FolderListingSearch>(
			DEFAULT_LISTING_SEARCH as FolderListingSearch
		)
	]

/** `listingSearchMiddlewares`' twin for the favorites page. */
export const favoritesSearchMiddlewares: SearchMiddleware<FavoritesSearch>[] = [
	({ next, search }) =>
		next(
			readListingSearch(search, FAVORITES_SORT_KEYS)
				.search as FavoritesSearch
		),
	stripSearchParams<FavoritesSearch>(
		DEFAULT_LISTING_SEARCH as FavoritesSearch
	)
]

/**
 * Each address parameter in the person's own words, reusing the labels the
 * controls themselves carry so that «the address asked for something I cannot
 * read» names the control they would have looked at.
 *
 * `by` and `direction` are two parameters of one control and share nothing but
 * that: the order button is named for the column, the sense of it for the
 * direction, and both are the words already on screen.
 */
export const SEARCH_PARAM_LABELS = {
	by: 'client:files.sort.label',
	categories: 'client:files.filters.category.label',
	direction: 'client:files.sort.direction.label',
	people: 'client:files.filters.people.label'
} as const satisfies Record<keyof FolderSearch, TKey>

/**
 * One of a closed list, or the default — and a note when the address named
 * something that is not on the list.
 *
 * Absent is not the same as unreadable and is not reported: an address that
 * says nothing about the order is every address the app writes for the default
 * order, which is most of them.
 */
function readChoice<TParam extends 'by' | 'direction'>(
	param: TParam,
	value: unknown,
	choices: readonly FolderSearch[TParam][],
	unreadable: UnreadableSearchValue[]
): FolderSearch[TParam] {
	if (value === undefined) {
		return DEFAULT_LISTING_SEARCH[param]
	}

	if (
		typeof value === 'string' &&
		(choices as readonly string[]).includes(value)
	) {
		return value as FolderSearch[TParam]
	}

	unreadable.push({ param, value })

	return DEFAULT_LISTING_SEARCH[param]
}

/**
 * The categories the address names, in the order the panel offers them.
 *
 * Built by filtering the declared list rather than by walking the address's
 * own, which is what collapses repeats and fixes the order in one step: the
 * result cannot hold a value twice and cannot hold them in any order but this
 * one, whatever the address spelled.
 *
 * **Exported for OPSSTD-7605's reason and nobody else's yet.** The search
 * results route takes the same two filters over a different schema — it has
 * no `by`/`direction` — so it cannot reuse `readListingSearch` whole. What it
 * needs is this one rule stated once, not a second copy of the dedup-and-order
 * logic drifting the day one of the two is touched.
 */
export function readCategories(
	value: unknown,
	unreadable: UnreadableSearchValue[]
): FolderSearch['categories'] {
	const named = new Set(readValues('categories', value, unreadable))
	const kept = NODE_CATEGORIES.filter((category) => named.has(category))

	for (const entry of named) {
		if (!(kept as readonly string[]).includes(entry)) {
			unreadable.push({ param: 'categories', value: entry })
		}
	}

	return kept
}

/**
 * The people the address names, deduplicated and in a fixed order.
 *
 * Sorted because subject ids have no order of their own and the address has to
 * be the same string for the same set. Nothing is drawn in this order — the
 * panel draws a person under the name it resolves for them — so this is about
 * the address and about nothing else.
 *
 * **An id that resolves to nobody is kept.** Only an empty value is refused
 * here, because it is the one that is unreadable without asking anybody: it
 * names no person, and a filter that names no person is drawn exactly like a
 * folder holding nothing. Whether a well-formed id still belongs to somebody
 * is an answer only the directory has, and dropping a filter because a lookup
 * was slow or refused would change what the listing shows for a reason that
 * has nothing to do with the address.
 *
 * **Past `MAX_PEOPLE` the rest is dropped and reported**, because the request
 * carrying them is refused whole: fifty-one names would take the listing down
 * with a bad request rather than narrow it, so an address that asks for more
 * opens on the fifty it can ask for. Reported rather than cut in silence — the
 * dropped ids go out as unreadable and the notice names the filter — for the
 * reason every other drop here is: a listing that quietly ignores what was
 * asked looks exactly like one that honoured it. The cut falls after the sort,
 * so the same set always keeps the same fifty.
 *
 * Exported for the same reason as `readCategories` above.
 */
export function readPeople(
	value: unknown,
	unreadable: UnreadableSearchValue[]
): FolderSearch['people'] {
	const named = new Set<string>()

	for (const entry of readValues('people', value, unreadable)) {
		if (entry === '') {
			unreadable.push({ param: 'people', value: entry })
			continue
		}

		named.add(entry)
	}

	const kept = [...named].sort()

	for (const entry of kept.slice(MAX_PEOPLE)) {
		unreadable.push({ param: 'people', value: entry })
	}

	return kept.slice(0, MAX_PEOPLE)
}

/**
 * The strings under a multi-value parameter.
 *
 * A lone value is accepted unbracketed, because that is how a person writes
 * one by hand: `?categories=image` parses to a string and not to a list of
 * one, and refusing it would make the address harder to type than to read.
 */
function readValues(
	param: 'categories' | 'people',
	value: unknown,
	unreadable: UnreadableSearchValue[]
): string[] {
	if (value === undefined) {
		return []
	}

	const strings: string[] = []

	for (const entry of Array.isArray(value) ? value : [value]) {
		if (typeof entry === 'string') {
			strings.push(entry)
			continue
		}

		unreadable.push({ param, value: entry })
	}

	return strings
}
